// Faturada/irsaliyede barkod okutarak satır ekleme. Ana barkod, ek barkod ve koli/paket barkodlarını tanır.
// Koli okutulunca ürün ambalaj adı ve içeriğiyle döner; satır ana birimde açılır (1 koli = 24 adet).
import React, { useRef, useState } from 'react';
import { CircularProgress, InputAdornment, TextField } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../config/api';
import { resolveBarcodes } from '../../inventory/services/packagingService';
import { mapInventoryDto } from '../../inventory/hooks/useInventoryData';
import type { InventoryItem } from '../../inventory/types/inventory.types';

export interface ScannedInvoiceItem {
  item: InventoryItem;
  /** Koli/paket adı; ana ya da ek barkodda yok. */
  packageName?: string;
  /** Ambalaj içeriği (ana birimde); ana barkodda 1. */
  factor: number;
  /** Ambalajın satış fiyatı (kendi fiyatı ya da içerik × adet fiyatı). */
  packageSalePrice?: number;
}

interface Props {
  onScanned: (scanned: ScannedInvoiceItem) => void;
  disabled?: boolean;
  placeholder?: string;
}

const BarcodeScanField: React.FC<Props> = ({ onScanned, disabled, placeholder }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const lookup = async (code: string) => {
    const [resolved] = await resolveBarcodes([code]);
    const match = resolved?.matches[0];
    if (!match) return null;
    // Ürünün tam bilgisi (fiyat, KDV, depo stokları, lot) paged aramadan; arama koli ve ek barkodları da bulur.
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEMS_PAGED, {
      pageNumber: 1,
      pageSize: 20,
      searchTerm: code,
      excludeVariantChildren: false,
    });
    const rows: any[] = response.data?.items ?? response.data?.Items ?? [];
    const row = rows.find((r) => String(r.id ?? r.Id ?? '').toLowerCase() === match.itemId.toLowerCase());
    if (!row) return null;
    return {
      item: mapInventoryDto(row),
      packageName: match.isPackage && match.unitName ? match.unitName : undefined,
      factor: match.isPackage && match.factor > 0 ? match.factor : 1,
      packageSalePrice: match.isPackage ? match.effectiveSalePrice : undefined,
    } as ScannedInvoiceItem;
  };

  const submit = async () => {
    const code = value.trim();
    if (!code || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const scanned = await lookup(code);
      if (!scanned) {
        setMessage(`'${code}' barkodlu ürün bulunamadı.`);
      } else {
        onScanned(scanned);
        setValue('');
      }
    } catch {
      setMessage('Barkod aranırken bir hata oluştu.');
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  return (
    <TextField
      inputRef={inputRef}
      size="small"
      value={value}
      disabled={disabled}
      placeholder={placeholder ?? 'Barkod okut (koli/paket barkodu da olur) ve Enter'}
      onChange={(e) => { setValue(e.target.value); setMessage(null); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          submit();
        }
      }}
      error={!!message}
      helperText={message || undefined}
      sx={{ minWidth: 280, flex: '1 1 280px', maxWidth: 420 }}
      inputProps={{ 'aria-label': 'Barkod okut' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {busy ? <CircularProgress size={16} /> : <QrCodeScannerIcon fontSize="small" />}
          </InputAdornment>
        ),
      }}
    />
  );
};

export default BarcodeScanField;
